import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import type { CommonCodeValueResponse } from '../types/code';

interface CodeItemDeleteConfirmProps {
  open: boolean;
  item: CommonCodeValueResponse | null;
  groupCode: string;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  errorMessage?: string;
}

export function CodeItemDeleteConfirm({
  open,
  item,
  groupCode,
  onClose,
  onConfirm,
  isLoading = false,
  errorMessage,
}: CodeItemDeleteConfirmProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  return (
    <FormDialog
      open={open}
      title={t('commonCode.deleteItemConfirm.title')}
      onClose={onClose}
      onSubmit={onConfirm}
      submitLabel={isLoading ? t('commonCode.deleteItemConfirm.deleting') : t('commonCode.deleteItemConfirm.confirm')}
      cancelLabel={t('commonCode.deleteItemConfirm.cancel')}
    >
      <div>
        {/* 에러 배너 */}
        {errorMessage && (
          <div
            role="alert"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}

        {item && (
          <>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-primary)',
                lineHeight: 1.6,
                marginBottom: 'var(--spacing-2)',
              }}
            >
              <strong
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  color: 'var(--color-danger)',
                }}
              >
                {groupCode}.{item.codeValue}
              </strong>{' '}
              ({item.defaultLabel}) 항목을 삭제하시겠습니까?
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
              }}
            >
              {t('commonCode.deleteItemConfirm.irreversible')}
            </p>
          </>
        )}
      </div>
    </FormDialog>
  );
}
